/**
 * ChangePasswordDialog — reusable modal for self-service password change.
 *
 * Posts to `POST /api/users/me/password`. The endpoint enforces the
 * full server-side contract:
 *   • verifies `current_password`
 *   • runs baseline + tenant password policy
 *   • blocks reuse against the rolling history (cap 24)
 *   • bumps `token_version` so other live sessions are revoked
 *   • writes a `user.password_change.self` audit row
 *
 * This component is used in two places:
 *   1. `PasswordRotationBanner` — when `force_password_change=true`,
 *      surfaced via the amber banner. The banner passes a
 *      `forceContext: true` prop so the copy reflects the
 *      tenant-policy rotation requirement.
 *   2. `ProfilePage` → "Change password" — always-available
 *      self-service entry point (Iter 32l). `forceContext` defaults
 *      to false and the copy reflects a proactive change.
 *
 * Backend is unchanged — pure UI extraction + reuse.
 */
import React, { useState } from 'react';
import { formatBackendDetail } from '../lib/apiErrors';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ChangePasswordDialog({
  onClose,
  onSaved,
  forceContext = false,
}) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (next !== confirm) {
      setError('New password and confirmation do not match.');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/users/me/password`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          current_password: current,
          new_password: next,
          confirm_password: confirm,
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // Use the shared apiErrors helper so policy-violation messages
        // (returned as `{detail: {messages: [...]}}`) render cleanly.
        const msg =
          formatBackendDetail(body?.detail) ||
          body?.detail ||
          'Could not change password.';
        setError(typeof msg === 'string' ? msg : JSON.stringify(msg));
        return;
      }
      // Success — show a transient message before signalling the
      // parent. We DO NOT auto-close immediately so the user has a
      // moment to read the "other sessions" hint.
      setSuccess(true);
      setCurrent('');
      setNext('');
      setConfirm('');
      // Caller decides whether to close the dialog or pivot UI.
      if (onSaved) onSaved();
    } catch {
      setError('Could not change password.');
    } finally {
      setSaving(false);
    }
  };

  const heading = success
    ? 'Password updated'
    : forceContext
      ? 'Change your password'
      : 'Change password';
  const subheading = success
    ? 'Password changed successfully. Other sessions may need to sign in again.'
    : forceContext
      ? "Your tenant's password policy requires a fresh password."
      : 'Set a new password for your account. Other live sessions will be signed out.';

  return (
    <div
      data-testid="change-password-dialog"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.45)',
        zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 420, background: '#fff', borderRadius: 8, padding: 20,
          boxShadow: '0 12px 32px rgba(15,23,42,0.18)',
          display: 'flex', flexDirection: 'column', gap: 12,
        }}
      >
        <div>
          <h3 style={{ fontSize: 14, fontWeight: 600, margin: 0 }}>{heading}</h3>
          <p style={{ fontSize: 12, color: 'var(--text-secondary)', margin: '4px 0 0' }}>
            {subheading}
          </p>
        </div>

        {!success && (
          <>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              Current password
              <input
                type="password" required value={current}
                onChange={(e) => setCurrent(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }}
                data-testid="change-password-current"
                autoComplete="current-password"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              New password
              <input
                type="password" required value={next}
                onChange={(e) => setNext(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }}
                data-testid="change-password-new"
                autoComplete="new-password"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
              Confirm new password
              <input
                type="password" required value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid var(--border, #cbd5e1)', borderRadius: 4 }}
                data-testid="change-password-confirm"
                autoComplete="new-password"
              />
            </label>
          </>
        )}

        {error && (
          <div style={{ fontSize: 12, color: '#b91c1c' }} data-testid="change-password-error">
            {String(error)}
          </div>
        )}
        {success && (
          <div
            style={{ fontSize: 12, color: '#15803d',
                     background: 'rgba(34,197,94,0.08)',
                     border: '1px solid rgba(34,197,94,0.25)',
                     borderRadius: 4, padding: '6px 8px' }}
            data-testid="change-password-success"
          >
            Password changed successfully. Other sessions may need to sign in again.
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          {success ? (
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={onClose}
              data-testid="change-password-done"
            >
              Done
            </button>
          ) : (
            <>
              <button type="button" className="btn btn-secondary btn-sm" onClick={onClose} data-testid="change-password-cancel">
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm" disabled={saving} data-testid="change-password-submit">
                {saving ? 'Saving…' : 'Save new password'}
              </button>
            </>
          )}
        </div>
      </form>
    </div>
  );
}
